import { useState, useCallback } from "react";
import { useQuery, useQueries, QueryObserverResult } from "react-query";

import { apiUrls as urls } from "config";
import { useCompute, useGetFetcher } from "api";
import { Api } from "interfaces";

export const insightUrls = {
  data_hygiene: "data-hygiene",
  data_rows: "data-rows",
  file_column_stats: "file-column-stats",
  file_outlier_summary: "file-outlier-summary",
  data_sample: "data_file_sample",
  data_column_types: "column-types",
  data_column_stats: "column-stats",
  data_column_profile: "column-profile",
  data_column_profiles: "column-profiles",
  data_column_outlier_rows: "column-outlier-rows",
  data_column_valid_counts: "column-valid-counts",
  data_joint_counts: "joint-counts",
  data_pivot: "pivot",
  data_pivot_export: "export-pivot",
  data_filtered_column_details: "filtered-column-details",
  data_y_impact: "y-impact",
  data_y_model: "y-model",
  data_group_discriminators: "group-discriminators",
  file_univariate_impact: "file-univariate-impact",
  data_compare_columns: "compare-columns",
  data_compare_column_sets: "compare-column-sets",
  data_bivariate_distributions: "bivariate-distributions",
  tri_variate_distributions: "tri-variate-distributions",
  data_relationship_matrix: "relationship_matrix",
  data_relationship_sort: "relationship_sort",
  data_cross_relationships: "cross-correlations",
  data_time_series_quality: "time-series/quality",
  data_time_series_distributions: "time-series/distributions",
  data_time_series_change_points: "time-series/change-points",
  data_time_series_anomalies: "time-series/anomalies",
  data_time_series_anomalies_multivariate: "time-series/multivariate-anomalies",
  data_time_series_components: "time-series/components",
  data_time_series_models: "time-series/models",
  data_time_series_clusters: "time-series/clusters",
  data_time_series_baseline: "time-series/baseline",
  data_time_series_relationships: "time-series/relationships",
  data_id_profiles: "id_profiles",
  data_export_id_profiles: "export_tagged_id_profiles",
  data_document_topics: "document_topics",
  data_export_document_topics: "export_tagged_document_topics",
  data_frequent_itemsets: "frequent_itemsets",
  data_association_rules: "association_rules",
  data_group_differences_summary: "group_differences_summary",
  data_group_differences_column_details: "group_differences_column_details",
  data_grouped_distributions: "grouped_distributions",
  data_file_comparisons: "file-comparison",
};

interface Params<K = { [key: string]: unknown }> {
  fileId: string;
  name: keyof typeof insightUrls;
  params?: K;
  enabled?: boolean;
}

type Error =
  | Api.ApiError
  | Api.ComputeStatus["error_message"]
  | Api.TaskStatus["result"];

export function useGetDataInsight<T = unknown, K = unknown>(props: Params<K>) {
  const { fileId, name, params, enabled = true } = props;
  const url = `${urls.data}/${fileId}/${insightUrls[name]}`;
  const compute = useCompute<K, T>(url, true);

  return useQuery<T, { message: Error }>(
    [url, params],
    async () => await compute({ data: params }),
    {
      staleTime: 24 * 60 * 60 * 1000,
      cacheTime: 24 * 60 * 60 * 1000,
      retry: false,
      enabled: fileId ? enabled : false,
    }
  );
}

export function useGetMultipleInsights<
  T = unknown,
  K = { [key: string]: unknown }
>(fileId: string, name: keyof typeof insightUrls, params: K[], enabled = true) {
  const url = `${urls.data}/${fileId}/${insightUrls[name]}`;
  const compute = useCompute<K, T>(url, true);

  return useQueries(
    params.map((p) => ({
      queryKey: [url, p],
      queryFn: () => compute({ data: p }),
      staleTime: 24 * 60 * 60 * 1000,
      cacheTime: 24 * 60 * 60 * 1000,
      retry: false,
      enabled: fileId ? enabled : false,
    }))
  ) as QueryObserverResult<T, Api.ApiError>[];
}

export function useGetInsightExport(
  fileId: string,
  insightName: keyof typeof insightUrls
) {
  const url = `${urls.data}/${fileId}/${insightUrls[insightName]}`;

  const fetcher = useGetFetcher();

  const [status, setStatus] = useState<
    "not-started" | "in-progress" | "completed"
  >("not-started");
  const [error, setError] = useState();

  const caller = useCallback(
    async (params: { [key: string]: any } = {}) => {
      setError(undefined);
      setStatus("in-progress");
      try {
        await fetcher({ url, method: "POST", data: params });
      } catch (e) {
        setError(e);
      }
      setStatus("completed");
    },
    [url, fetcher]
  );

  return { caller, status, error };
}
